/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, take, finalize } from 'rxjs';
import { ErrorObject } from 'src/app/_models/error';
import { FormBase } from 'src/app/_models/form-base';
import { CreateLaboratory, Laboratory } from 'src/app/_models/laboratory';
import { User } from 'src/app/_models/user';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { LaboratoryService } from 'src/app/_services/laboratory.service';
import { ModalService } from 'src/app/_services/modal.service';
import { ModalComponent } from '../../_components/modals/modal.component';
import { ButtonComponent } from '../../_components/buttons/button/button.component';
import { UnderlinedFormFieldComponent } from '../../_components/forms/underlined-form-field/underlined-form-field.component';
import { AuthEventService } from 'src/app/_services/auth-event.service';
import { Role } from 'src/app/_models/role';

@Component({
  selector: 'app-add-lab',
  templateUrl: './add-lab.component.html',
  styleUrls: ['./add-lab.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    UnderlinedFormFieldComponent,
    ButtonComponent,
    ModalComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddLabComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly modalService = inject(ModalService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly laboratoryService = inject(LaboratoryService);
  private readonly authEventService = inject(AuthEventService);

  private readonly allowNavigation = new Subject<boolean>();

  currentUser: User;
  organisationId: string;
  laboratory = signal<Laboratory | null>(null);

  isProcessing = signal(false);
  error = signal<ErrorObject | null>(null);
  addLabSuccessMessage = signal('');

  labNameField = new FormBase({
    key: 'labName',
    label: 'Lab Name',
    type: 'text',
    placeholder: 'Lab Name',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  addLabForm = this.formBuilder.group({
    [this.labNameField.key]: ['', Validators.required],
  });

  ngOnInit(): void {
    this.currentUser = this.authenticationService.currentUserValue;
    // Use route data to get and set list of device ids
    if (window.history.state.data) {
      this.organisationId = window.history.state.data as string;
    }

    this.laboratory.set(this.authenticationService.selectedLaboratory);
    // Check for cached form on init (after login redirect)
    const unsavedCachedData =
      this.laboratoryService.retrieveAndClearUnsavedLabCache;

    if (unsavedCachedData) {
      this.updateFormValues(unsavedCachedData);
    }
  }

  openModal(modalId: string): void {
    this.modalService.open(modalId);
  }

  closeModal(modalId: string): void {
    this.modalService.close(modalId);
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'view-lab-button':
        void this.router.navigate(['laboratories/view-lab']);
        break;
      case 'add-users-button':
        void this.router.navigate(['user/add-user']);
        break;
      case 'close-button':
        this.closeModal('success');
        break;
      case 'error-button':
        this.closeModal('error');
        break;
      case 'cancel-button':
        this.allowNavigation.next(false);
        this.closeModal('attention');
        break;
      case 'confirm-navigation':
        this.allowNavigation.next(true);
        this.closeModal('attention');
        break;
    }
  }

  onSubmit(): void {
    // If form is already being submitted, exit
    if (this.isProcessing()) {
      return;
    }

    this.isProcessing.set(true);
    this.error.set(null);
    this.addLabSuccessMessage.set('');

    const orgId =
      this.currentUser.organisationId.toString() !==
      this.organisationId?.toString()
        ? this.organisationId
        : null;

    this.laboratoryService
      .addLab(this.addLabForm.value.labName, orgId)
      .pipe(finalize(() => this.isProcessing.set(false)))
      .subscribe({
        next: (response: Laboratory) => {
          this.addLabSuccessMessage.set('New laboratory has been created.');
          this.addLabForm.reset();
          if (this.currentUser?.role === Role.OGI_Super_Administrator) {
            this.openModal('success');
          } else if (this.currentUser?.role === Role.Super_User) {
            response.isOwnLab = false;
            if (this.currentUser?.laboratories) {
              // Build a new laboratories array rather than pushing onto the
              // existing one — currentUser is the same object reference held
              // by AuthenticationService, so mutating it in place would leave
              // currentUser$'s distinctUntilChanged comparing that object
              // against itself, silently suppressing the emission.
              this.currentUser = {
                ...this.currentUser,
                laboratories: [...this.currentUser.laboratories, response],
              };
              this.authenticationService.updateCurrentUser(this.currentUser);
            }
            this.authenticationService.setSelectedLaboratory(response);
            this.openModal('super-user-success');
          }
        },
        error: (err: unknown) => {
          const errObj = err as Partial<ErrorObject>;
          this.error.set({
            status: errObj?.status ?? 0,
            message: errObj?.message || 'Something went wrong',
            code: errObj?.code ?? null,
          });
          this.openModal('error');
        },
      });
  }

  updateFormValues(lab: CreateLaboratory): void {
    this.addLabForm.patchValue({
      labName: lab.name,
    });

    this.addLabForm.markAsDirty();
  }

  getCurrentFormValues(): CreateLaboratory {
    const labCache: CreateLaboratory = {
      name: this.addLabForm.controls['labName'].value,
    };
    return labCache;
  }

  canExit(): Promise<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  ngOnDestroy(): void {
    // Cache form if we're being destroyed due to 401 navigation
    if (
      this.authEventService.isUnauthorizedNavigationPending &&
      !this.addLabForm.pristine
    ) {
      const formValues = this.getCurrentFormValues();
      this.laboratoryService.onUpdateCreateLabCache(formValues);
    }

    this.allowNavigation.complete();
  }

  private isNavigationAllowed(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (
        this.addLabForm.pristine ||
        !this.currentUser ||
        this.authEventService.isUnauthorizedNavigationPending
      ) {
        resolve(true);
      } else {
        this.openModal('attention');
        this.allowNavigation.pipe(take(1)).subscribe((isConfirmed) => resolve(isConfirmed));
      }
    });
  }
}
